import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Box, Collapse, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { IOrganizationConfig } from '@tymely/atoms';
import { CopyToClipboardButton } from '@tymely/components';

import { cleanHtml, markdownToHtml } from './util';

export default function TemplateViewer({
    template,
    markupLanguage,
}: {
    template: string;
    markupLanguage?: IOrganizationConfig['response_markup_language'];
}) {
    if (markupLanguage === 'MARKDOWN') {
        template = markdownToHtml(template);
    }
    const ref = useRef<HTMLDivElement>(null);
    const [expandedBlocks, setExpandedBlocks] = useState<Record<string, boolean>>({});
    useEffect(() => {
        ref.current?.querySelectorAll('div[collapse]').forEach((el) => {
            const collapseText = el.getAttribute('collapse') || 'Collapse';
            const htmlContent = el.innerHTML;
            const container = document.createElement('div');
            createRoot(container).render(
                <CollapsibleContent
                    html={htmlContent}
                    collapseText={collapseText}
                    expanded={expandedBlocks[collapseText] || false}
                    onToggle={(expanded) =>
                        setExpandedBlocks({
                            ...expandedBlocks,
                            [collapseText]: expanded,
                        })
                    }
                />,
            );
            el.replaceWith(container);
        });
    }, [template]);

    const cleanedHtml = cleanHtml(template);

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: 10,
                    backgroundColor: 'background.paper',
                    borderRadius: 1,
                    p: 0.5,
                    boxShadow: 1,
                }}
            >
                <CopyToClipboardButton text={cleanedHtml} size="small" />
            </Box>
            <div
                ref={ref}
                dangerouslySetInnerHTML={{ __html: cleanedHtml }}
                style={{
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                }}
            />
        </Box>
    );
}

function CollapsibleContent({
    html,
    collapseText,
    expanded,
    onToggle,
}: {
    html: string;
    collapseText: string;
    expanded: boolean;
    onToggle(expanded: boolean): void;
}) {
    const [isExpanded, setIsExpanded] = useState(expanded);

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsExpanded((prev) => {
            const next = !prev;
            onToggle(next);
            return next;
        });
    };

    return (
        <Box sx={{ border: '1px solid #eee', borderRadius: 1, my: 1 }} onMouseDown={handleMouseDown}>
            <Box
                display="flex"
                alignItems="center"
                sx={{
                    p: 1,
                    cursor: 'pointer',
                    backgroundColor: '#f5f5f5',
                    '&:hover': { backgroundColor: '#eeeeee' },
                }}
            >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                <Typography variant="body2">{collapseText}</Typography>
            </Box>

            <Collapse in={isExpanded}>
                <Box sx={{ p: 2 }} dangerouslySetInnerHTML={{ __html: html }} />
            </Collapse>
        </Box>
    );
}
